/**
 * matchLines — geometry for the glowing "we found you a trade" connectors that
 * link the current user's pin to matched collectors.
 *
 * Kept separate from the overlay component so the maths is testable without a
 * Mapbox instance (projection is injected, never imported).
 */

/** Lines beyond this just make the map look like a spider web. */
export const MAX_MATCH_LINES = 6;

const idStr = (v) => (v == null ? "" : String(v));

/** Coerce either coordinate shape the app uses into {longitude, latitude}. */
export const toLngLat = (coordinates) => {
  if (!coordinates) return null;
  if (Array.isArray(coordinates)) {
    const [longitude, latitude] = coordinates;
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
    return { longitude, latitude };
  }
  const { longitude, latitude } = coordinates;
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  return { longitude, latitude };
};

/**
 * Which users get a line drawn to them.
 *
 * Excludes the current user (no self-loop), anything without usable
 * coordinates, and caps the count so a dense city doesn't turn into noise.
 */
export const selectMatchLineTargets = ({
  users = [],
  matchedUserIds = [],
  currentUserId = null,
  max = MAX_MATCH_LINES,
} = {}) => {
  const matched = new Set(matchedUserIds.map(idStr));
  if (matched.size === 0) return [];

  return users
    .filter((u) => u && matched.has(idStr(u.id)))
    .filter((u) => idStr(u.id) !== idStr(currentUserId))
    .filter((u) => toLngLat(u.coordinates) !== null)
    .slice(0, Math.max(0, max));
};

/**
 * SVG path for one connector: a gentle arc, not a straight line.
 *
 * Straight lines between pins read as map furniture (roads, borders). A bowed
 * curve reads as a relationship, and it also stops two lines to nearby pins
 * from overlapping into one thick smear.
 *
 * `curvature` is a fraction of the segment length; the arc always bows to the
 * same side so a fan of lines looks deliberate rather than tangled.
 */
export const buildMatchLinePath = (from, to, curvature = 0.22) => {
  if (!from || !to) return "";
  const { x: x1, y: y1 } = from;
  const { x: x2, y: y2 } = to;
  if (![x1, y1, x2, y2].every(Number.isFinite)) return "";

  const dx = x2 - x1;
  const dy = y2 - y1;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  // Perpendicular offset, scaled by segment length.
  const len = Math.hypot(dx, dy) || 1;
  const offset = len * curvature;
  const ctrlX = midX + (-dy / len) * offset;
  const ctrlY = midY + (dx / len) * offset;

  const r = (n) => Math.round(n * 100) / 100;
  return `M ${r(x1)} ${r(y1)} Q ${r(ctrlX)} ${r(ctrlY)} ${r(x2)} ${r(y2)}`;
};

/**
 * Build every drawable segment, projecting geo → screen via the injected fn.
 * Segments whose projection fails (off-globe, map not ready) are dropped.
 *
 * @param {(lngLat: {longitude:number, latitude:number}) => {x:number,y:number}} project
 */
export const buildMatchSegments = ({
  origin,
  targets = [],
  project,
  curvature = 0.22,
  staggerMs,
} = {}) => {
  const originLngLat = toLngLat(origin);
  if (!originLngLat || typeof project !== "function") return [];

  let originPt;
  try {
    originPt = project(originLngLat);
  } catch {
    return [];
  }
  if (!originPt || !Number.isFinite(originPt.x) || !Number.isFinite(originPt.y)) {
    return [];
  }

  const segments = [];
  targets.forEach((user, i) => {
    const lngLat = toLngLat(user.coordinates);
    if (!lngLat) return;
    let pt;
    try {
      pt = project(lngLat);
    } catch {
      return;
    }
    if (!pt || !Number.isFinite(pt.x) || !Number.isFinite(pt.y)) return;

    const d = buildMatchLinePath(originPt, pt, curvature);
    if (!d) return;

    segments.push({
      id: idStr(user.id) || `seg-${i}`,
      d,
      // Both endpoints in screen space. The core stroke paints a gradient from
      // origin to destination so the trail's direction is readable even in a
      // frozen frame — bright end = destination.
      start: originPt,
      end: pt,
      // Stagger so lines draw one after another, like the app is finding them.
      delayMs: i * (staggerMs ?? 140),
    });
  });
  return segments;
};

/**
 * Geographic bounds that contain the origin and its destinations, for
 * `map.fitBounds`. Returned as Mapbox's `[[west, south], [east, north]]`.
 *
 * Returns null when there is nothing to frame, so the caller can leave the
 * camera exactly where it is rather than flying to a degenerate box — a bad
 * fit is far more disorienting than no move at all.
 *
 * A single point produces a zero-area box; Mapbox handles that correctly given
 * a `maxZoom`, which the caller supplies alongside the padding.
 */
export const fitBoundsFor = (origin, targets = []) => {
  const points = [];
  const originLngLat = toLngLat(origin);
  if (originLngLat) points.push(originLngLat);

  (targets || []).forEach((t) => {
    const lngLat = toLngLat(t?.coordinates ?? t);
    if (lngLat) points.push(lngLat);
  });

  if (points.length === 0) return null;

  const lngs = points.map((p) => p.longitude);
  const lats = points.map((p) => p.latitude);

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};
